import { PDFDocument } from "pdf-lib"
import { useCallback, useState } from "react"

interface SplitOptions {
  ranges: { start: number; end: number }[]
}

export function usePdfSplit() {
  const [processing, setProcessing] = useState(false)
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState<string | null>(null)

  const splitPdf = useCallback(async (file: File, options: SplitOptions) => {
    setProcessing(true)
    setProgress(0)
    setError(null)

    try {
      const arrayBuffer = await file.arrayBuffer()
      const sourcePdf = await PDFDocument.load(arrayBuffer)
      const results: Blob[] = []

      for (let i = 0; i < options.ranges.length; i++) {
        const range = options.ranges[i]
        const newPdf = await PDFDocument.create()

        const pageIndices = []
        for (let p = range.start - 1; p < range.end; p++) {
          pageIndices.push(p)
        }

        const pages = await newPdf.copyPages(sourcePdf, pageIndices)
        pages.forEach((page) => newPdf.addPage(page))

        const pdfBytes = await newPdf.save()
        results.push(
          new Blob([pdfBytes as BlobPart], { type: "application/pdf" }),
        )
        setProgress(((i + 1) / options.ranges.length) * 100)
      }

      return results
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to split PDF"
      setError(message)
      throw err
    } finally {
      setProcessing(false)
    }
  }, [])

  return { splitPdf, processing, progress, error }
}
